import { useState, useEffect } from "react";
import { collection, getDocs, doc, getDoc, query, orderBy } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../../contexts/AuthContext";
import { C, F, Label, Rule, Spinner, KPI } from "../ui";
import { fmtUSD, fmtPct } from "../../utils/ddp";

const COMISION_PCT = 0.04; // 4% sobre FOB como default

export function VendedorClientes() {
  const { user } = useAuth();
  const [clientes, setClientes]   = useState([]);
  const [pedidos, setPedidos]     = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [usersSnap, pedidosSnap] = await Promise.all([
        getDocs(collection(db, "usuarios")),
        getDocs(query(collection(db, "pedidos"), orderBy("creadoEn", "desc"))),
      ]);
      const todosUsuarios = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const todosPedidos  = pedidosSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Filtrar clientes asignados a este vendedor
      const misClientes = todosUsuarios.filter(u =>
        u.vendedor_id === user?.uid && u.rol === "comprador"
      );
      setClientes(misClientes);
      setPedidos(todosPedidos.filter(p => misClientes.some(c => c.uid === p.usuario_id || c.email === p.cliente_email)));
      setLoading(false);
    };
    load();
  }, [user]);

  const volumenTotal = pedidos.reduce((s, p) => s + (p.fob_total || 0), 0);
  const comisionTotal = volumenTotal * COMISION_PCT;

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: F.serif, fontSize: 24, color: C.white, marginBottom: 4 }}>Mis clientes</div>
        <div style={{ fontFamily: F.mono, fontSize: 10, color: C.muted }}>{clientes.length} clientes asignados</div>
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "0.5px", background: C.border, marginBottom: 24 }}>
        <KPI label="Clientes activos"    value={clientes.length} />
        <KPI label="Pedidos totales"     value={pedidos.length} />
        <KPI label="Volumen FOB"         value={fmtUSD(volumenTotal)} />
        <KPI label="Comisión estimada"   value={fmtUSD(comisionTotal)} valueColor={C.green} sub={`${(COMISION_PCT*100).toFixed(0)}% sobre FOB`} />
      </div>

      {clientes.length === 0 ? (
        <div style={{ background: C.s1, border: `0.5px solid ${C.border}`, padding: 40, textAlign: "center" }}>
          <div style={{ fontFamily: F.serif, fontSize: 18, color: C.white, marginBottom: 8 }}>Sin clientes asignados</div>
          <div style={{ fontFamily: F.sans, fontSize: 12, color: C.muted }}>
            El administrador te asigna clientes desde el panel de usuarios.
          </div>
        </div>
      ) : (
        <div style={{ background: C.border }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 160px 100px 100px 80px", background: C.s2, padding: "7px 14px", gap: 8 }}>
            {["Cliente","Email","Pedidos","FOB Total","Comisión"].map(h => (
              <div key={h} style={{ fontFamily: F.sans, fontSize: 8, color: C.muted, letterSpacing: 2, textTransform: "uppercase" }}>{h}</div>
            ))}
          </div>
          {clientes.map(c => {
            const pedidosCliente = pedidos.filter(p => p.usuario_id === c.uid || p.cliente_email === c.email);
            const volumen = pedidosCliente.reduce((s, p) => s + (p.fob_total || 0), 0);
            return (
              <div key={c.id} style={{ display: "grid", gridTemplateColumns: "1fr 160px 100px 100px 80px", background: C.s1, padding: "12px 14px", gap: 8, borderBottom: `0.5px solid ${C.border}`, alignItems: "center" }}>
                <div>
                  <div style={{ fontFamily: F.sans, fontSize: 12, color: C.white }}>{c.nombre || "—"}</div>
                  <div style={{ fontFamily: F.mono, fontSize: 9, color: C.muted }}>{c.empresa || ""}</div>
                </div>
                <div style={{ fontFamily: F.mono, fontSize: 10, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.email}</div>
                <div style={{ fontFamily: F.mono, fontSize: 11, color: C.white }}>{pedidosCliente.length}</div>
                <div style={{ fontFamily: F.mono, fontSize: 11, color: C.off }}>{fmtUSD(volumen)}</div>
                <div style={{ fontFamily: F.mono, fontSize: 11, color: C.green }}>{fmtUSD(volumen * COMISION_PCT)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function VendedorComisiones() {
  const { user } = useAuth();
  const [pedidos, setPedidos]   = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const usersSnap = await getDocs(collection(db, "usuarios"));
      const misClientesIds = usersSnap.docs
        .map(d => d.data())
        .filter(u => u.vendedor_id === user?.uid)
        .map(u => u.uid);

      const pedidosSnap = await getDocs(query(collection(db, "pedidos"), orderBy("creadoEn", "desc")));
      const todosPedidos = pedidosSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setPedidos(todosPedidos.filter(p => misClientesIds.includes(p.usuario_id)));
      setLoading(false);
    };
    load();
  }, [user]);

  // Agrupar por ciclo
  const porCiclo = pedidos.reduce((acc, p) => {
    const ciclo = p.ciclo_nombre || "Sin ciclo";
    if (!acc[ciclo]) acc[ciclo] = { pedidos: [], fob: 0 };
    acc[ciclo].pedidos.push(p);
    acc[ciclo].fob += p.fob_total || 0;
    return acc;
  }, {});

  const totalFOB = pedidos.reduce((s, p) => s + (p.fob_total || 0), 0);

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: F.serif, fontSize: 24, color: C.white, marginBottom: 4 }}>Comisiones</div>
        <div style={{ fontFamily: F.mono, fontSize: 10, color: C.muted }}>Historial por ciclo de contenedor</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.5px", background: C.border, marginBottom: 24 }}>
        <KPI label="Volumen FOB total"   value={fmtUSD(totalFOB)} />
        <KPI label="Comisión acumulada"  value={fmtUSD(totalFOB * COMISION_PCT)} valueColor={C.green} />
        <KPI label="Tasa de comisión"    value={`${(COMISION_PCT*100).toFixed(0)}%`} sub="Sobre FOB" />
      </div>

      {Object.keys(porCiclo).length === 0 ? (
        <div style={{ background: C.s1, border: `0.5px solid ${C.border}`, padding: 40, textAlign: "center" }}>
          <div style={{ fontFamily: F.sans, fontSize: 12, color: C.muted }}>Sin comisiones registradas aún.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5px", background: C.border }}>
          {Object.entries(porCiclo).map(([ciclo, data]) => (
            <div key={ciclo} style={{ background: C.s1, padding: "18px 20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ fontFamily: F.serif, fontSize: 16, color: C.white }}>{ciclo}</div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontFamily: F.mono, fontSize: 16, color: C.green }}>{fmtUSD(data.fob * COMISION_PCT)}</div>
                  <div style={{ fontFamily: F.mono, fontSize: 9, color: C.muted }}>de {fmtUSD(data.fob)} FOB</div>
                </div>
              </div>
              <div style={{ fontFamily: F.mono, fontSize: 9, color: C.muted }}>{data.pedidos.length} pedidos en este ciclo</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function VendedorPedidos() {
  const { user } = useAuth();
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const usersSnap = await getDocs(collection(db, "usuarios"));
      const misClientesEmails = usersSnap.docs
        .map(d => d.data())
        .filter(u => u.vendedor_id === user?.uid)
        .map(u => u.email);

      const snap = await getDocs(query(collection(db, "pedidos"), orderBy("creadoEn", "desc")));
      const todos = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setPedidos(todos.filter(p => misClientesEmails.includes(p.cliente_email)));
      setLoading(false);
    };
    load();
  }, [user]);

  const ESTADO_COLOR = { confirmado:"#60A5FA", en_produccion:"#F59E0B", embarcado:"#8B5CF6", en_aduana:"#EC4899", listo_deposito:"#3D7A5F", entregado:"#555" };

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: F.serif, fontSize: 24, color: C.white, marginBottom: 4 }}>Pedidos activos</div>
        <div style={{ fontFamily: F.mono, fontSize: 10, color: C.muted }}>{pedidos.filter(p=>p.estado!=="entregado").length} pedidos en curso</div>
      </div>

      {pedidos.length === 0 ? (
        <div style={{ background: C.s1, border: `0.5px solid ${C.border}`, padding: 40, textAlign: "center" }}>
          <div style={{ fontFamily: F.sans, fontSize: 12, color: C.muted }}>Sin pedidos activos de tus clientes.</div>
        </div>
      ) : (
        <div style={{ background: C.border }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 140px 100px 100px 120px", background: C.s2, padding: "7px 14px", gap: 8 }}>
            {["Cliente","Ciclo","FOB","DDP","Estado"].map(h => (
              <div key={h} style={{ fontFamily: F.sans, fontSize: 8, color: C.muted, letterSpacing: 2, textTransform: "uppercase" }}>{h}</div>
            ))}
          </div>
          {pedidos.map(p => (
            <div key={p.id} style={{ display: "grid", gridTemplateColumns: "1fr 140px 100px 100px 120px", background: C.s1, padding: "12px 14px", gap: 8, borderBottom: `0.5px solid ${C.border}`, alignItems: "center" }}>
              <div>
                <div style={{ fontFamily: F.sans, fontSize: 12, color: C.white }}>{p.cliente_nombre || "—"}</div>
                <div style={{ fontFamily: F.mono, fontSize: 9, color: C.muted }}>{p.cliente_email}</div>
              </div>
              <div style={{ fontFamily: F.mono, fontSize: 10, color: C.muted }}>{p.ciclo_nombre || "—"}</div>
              <div style={{ fontFamily: F.mono, fontSize: 11, color: C.off }}>{fmtUSD(p.fob_total)}</div>
              <div style={{ fontFamily: F.mono, fontSize: 11, color: C.white }}>{fmtUSD(p.ddp_total)}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 5, height: 5, borderRadius: "50%", background: ESTADO_COLOR[p.estado] || C.muted }} />
                <span style={{ fontFamily: F.mono, fontSize: 9, color: ESTADO_COLOR[p.estado] || C.muted, textTransform: "uppercase" }}>{p.estado?.replace("_"," ") || "—"}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
